import { yupResolver } from "@hookform/resolvers/yup";
import {
  Button,
  Card,
  Flex,
  TextField,
  Title,
  Typography,
} from "@qws-org/ui-kit";
import type { FC } from "react";
import { useEffect } from "react";
import { Controller, useForm } from "react-hook-form";

import type { AuthFeatureData } from "~/core";
import { createRestoreValidationSchema } from "~/features/restore-password-form/restore.schema";
import type { RestoreCredential } from "~/shared/utils/auth-poker-sdk/auth-poker-sdk";

export type ServerErrors = string | null;

export type RestoreData = ExtractArrayItemType<
  NonNullable<AuthFeatureData>["restore_password"]
>;

export type RestorePasswordFormProps = {
  data: RestoreData;
  serverResponse?: ServerErrors;
  onSubmit: (data: RestoreCredential) => void;
};

export const RestorePasswordForm: FC<RestorePasswordFormProps> = (props) => {
  const { serverResponse, data, onSubmit } = props;

  const { handleSubmit, control, setError, clearErrors } =
    useForm<RestoreCredential>({
      mode: "onSubmit",
      resolver: yupResolver(
        createRestoreValidationSchema({
          validation_required:
            data?.validation_required ?? "Field is required!",
          validation_invalid: data?.validation_invalid ?? "Email is invalid",
        }),
      ),
    });

  useEffect(() => {
    if (serverResponse) {
      setError("email", {
        type: "server",
        message: serverResponse,
      });
    } else {
      clearErrors("email");
    }
  }, [serverResponse, setError, clearErrors]);

  return (
    <Card
      variant="secondary"
      minWidth={312}
      maxWidth={440}
      minHeight={570}
      md={{ minHeight: 640, maxWidth: 525 }}
      className="rounded-2xl md:rounded-lg"
      data-testid="restore-password-form"
    >
      <form
        onSubmit={handleSubmit(onSubmit)}
        className="h-full flex-grow flex flex-col"
      >
        <Flex
          p={{ px: 16, py: 24 }}
          md={{ p: { py: 32, px: 40 } }}
          flexDirection="column"
          className="flex-grow"
        >
          <Title
            as="h2"
            size="small"
            weight="bold"
            p={{ pb: 16 }}
            md={{ p: { pb: 24, pt: 0 }, horizontalAlign: "left" }}
            data-testid="restore-password-form-title"
          >
            {data?.form_title}
          </Title>

          <Typography
            size="large"
            className="text-center md:text-start"
            m={{ mb: 8 }}
            data-testid="restore-password-form-description"
          >
            {data?.form_description ?? "Please enter your email"}
          </Typography>
          <Controller
            control={control}
            name="email"
            render={({ field, fieldState: { error } }) => (
              <TextField
                minHeight="fit-content"
                maxWidth={312}
                minWidth="100%"
                {...field}
                label={data?.form_email_input_label ?? "Email"}
                errorMessage={error?.message}
                data-testid="restore-password-form-email-input"
              />
            )}
          />

          <Button
            type="submit"
            size="small"
            md={{
              fontSize: "button.desktop.medium",
              p: { py: 16 },
              m: { mt: "auto" },
            }}
            m={{ mt: "auto" }}
            p={{ py: 12 }}
            weight="semibold"
            width="100%"
            maxHeight="none"
            minHeight="none"
            height="fit-content"
            data-testid="restore-password-form-submit-button"
          >
            {data?.form_submit_button_text}
          </Button>
        </Flex>
      </form>
    </Card>
  );
};
